import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;
export const ASSET_BASE = BACKEND_URL;

const client = axios.create({ baseURL: API, timeout: 60000 });
export { client };

const isAdminPath = (url = "") =>
  url.startsWith("/admin/") ||
  url.startsWith("/superadmin/") ||
  url.startsWith("/franchise/");
const isVerifierPath = (url = "") => url.startsWith("/verify");
// Customer-scoped paths need their own JWT (role=customer). We
// deliberately keep this narrow so a customer token never leaks into a
// vendor/admin endpoint — the /api/game/:id/spin route ALSO gets the
// token attached (opportunistic My Winnings stamping) via
// `isSpinPath` below.
const isCustomerPath = (url = "") =>
  url.startsWith("/customer/") || url.startsWith("/offers/");
const isSpinPath = (url = "") => /\/game\/[^/]+\/spin$/.test(url);

client.interceptors.request.use((config) => {
  if (config.headers.Authorization) return config;
  const url = config.url || "";
  const adminToken = localStorage.getItem("citydooh_admin_token");
  const vendorToken = localStorage.getItem("citydooh_token");
  const verifierToken = localStorage.getItem("citydooh_verifier_token");
  const customerToken = localStorage.getItem("citydooh_customer_token");
  if (isVerifierPath(url) && verifierToken) {
    config.headers.Authorization = `Bearer ${verifierToken}`;
  } else if (isAdminPath(url) && adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  } else if (isCustomerPath(url) && customerToken) {
    config.headers.Authorization = `Bearer ${customerToken}`;
  } else if (isSpinPath(url) && customerToken) {
    // Opportunistic — if a customer happens to be signed in when they
    // play, we forward their token so the backend can stamp the lead
    // (see routes.game.spin -> current_customer_id_optional). If they
    // aren't signed in, the header is simply absent and anonymous
    // play works unchanged.
    config.headers.Authorization = `Bearer ${customerToken}`;
  } else if (vendorToken) {
    config.headers.Authorization = `Bearer ${vendorToken}`;
  }
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // eslint-disable-next-line no-console
      console.warn("Unauthorized");
    }
    return Promise.reject(err);
  }
);

export const assetUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path}`;
};

export const apiClient = client;

const withAdminToken = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const api = {
  // vendor auth
  register: (data) => client.post("/auth/vendor/register", data),
  // Public preview for a signup billing-coupon — used by the vendor
  // sign-up form to inline-validate the code the vendor typed before
  // they submit the registration.
  previewSignupCoupon: (code) => client.get(`/coupons/signup-preview?code=${encodeURIComponent(code)}`),
  // Guided Setup — POSTs {registration, intake}; returns same
  // {token, vendor} shape as /register so the same login-side-effect
  // wiring in AuthContext just works.
  registerGuided: (data) => client.post("/auth/vendor/register-guided", data),
  login: (data) => client.post("/auth/vendor/login", data),
  me: () => client.get("/auth/vendor/me"),
  updateProfile: (data) => client.put("/auth/vendor/me", data),
  updatePassword: (data) => client.put("/auth/vendor/password", data),
  forgotPassword: (email) => client.post("/auth/vendor/forgot-password", { email }),
  resetPassword: (token, new_password) =>
    client.post("/auth/vendor/reset-password", { token, new_password }),
  sendVerifyOtp: () => client.post("/auth/vendor/send-verify-otp"),
  verifyEmailOtp: (code) => client.post("/auth/vendor/verify-email-otp", { code }),

  // vendor campaigns
  listCampaigns: () => client.get("/campaigns"),
  getCampaign: (id) => client.get(`/campaigns/${id}`),
  createCampaign: (data) => client.post("/campaigns", data),
  // A3 — admin proxy. The vendor is still the owner + payer.
  adminCreateCampaignForVendor: (targetVendorId, data) =>
    client.post(`/campaigns/admin/vendors/${targetVendorId}/campaigns`, data),
  updateCampaign: (id, data) => client.put(`/campaigns/${id}`, data),
  deleteCampaign: (id) => client.delete(`/campaigns/${id}`),
  generateQr: (id) => client.post(`/campaigns/${id}/qr`),

  // public game
  publicCampaign: (id) => client.get(`/game/${id}`),
  spin: (id, payload) => client.post(`/game/${id}/spin`, payload),
  sendLeadOtp: (id, data) => client.post(`/game/${id}/send-otp`, data),
  verifyLeadOtp: (id, data) => client.post(`/game/${id}/verify-otp`, data),
  publicCoupon: (code) => client.get(`/game/coupon/${code}`),

  // leads / coupons
  listLeads: (params) => client.get("/leads", { params }),
  exportLeadsCsv: (params) => client.get("/leads.csv", { params, responseType: "blob" }),
  verifyByPhone: (phone) => client.get("/leads/verify", { params: { phone } }),
  redeemCoupon: (couponId) => client.post(`/coupons/${couponId}/redeem`),

  // analytics
  overview: (days = 30) => client.get("/analytics/overview", { params: { days } }),
  campaignStats: (id) => client.get(`/analytics/campaign/${id}`),

  // messages
  sendMessages: (payload) => client.post("/messages/send", payload),
  previewRetargetingEmail: (payload) =>
    client.post("/messages/preview-email", payload),
  listMessages: (params) => client.get("/messages", { params }),
  messagesConfig: () => client.get("/messages/config"),

  // Wallet
  walletSummary: () => client.get("/wallet"),
  walletTopupCreate: (payload) => client.post("/wallet/topup", payload),
  walletTopupConfirm: (payload) => client.post("/wallet/topup/confirm", payload),
  walletLedger: (qs) => client.get(`/wallet/ledger${qs ? `?${qs}` : ""}`),

  // Resume a wallet-paused campaign
  resumeCampaign: (campaignId) =>
    client.post(`/payments/campaigns/${campaignId}/resume`),

  // billing / payments (vendor-side)
  meBilling: () => client.get("/auth/vendor/me/billing"),
  paymentsConfig: () => client.get("/payments/config"),
  checkoutSummary: (campaignId) =>
    client.get(`/payments/campaigns/${campaignId}/checkout`),
  createOrder: (campaignId) =>
    client.post(`/payments/campaigns/${campaignId}/create-order`),
  convertToPaid: (campaignId, payload) =>
    client.post(`/payments/campaigns/${campaignId}/convert-to-paid`, payload),
  // Phase 18 Round 2A — wallet-debit path for campaign fee activation.
  payCampaignWithWallet: (campaignId) =>
    client.post(`/payments/campaigns/${campaignId}/pay-with-wallet`),
  listInvoices: () => client.get("/payments/invoices"),
  // Vendor-owned PDF download — returns a Blob; requires the vendor's
  // own auth token (backend 404s on someone else's invoice).
  downloadInvoicePdf: (invoiceNumber) =>
    client.get(`/payments/invoices/${encodeURIComponent(invoiceNumber)}/download`, {
      responseType: "blob",
    }),
  // Admin ledger — returns every invoice across every vendor.
  adminListInvoices: (params) => client.get("/admin/invoices", { params }),
  adminDownloadInvoicePdf: (invoiceNumber) =>
    client.get(`/admin/invoices/${encodeURIComponent(invoiceNumber)}/download`, {
      responseType: "blob",
    }),

  // ── A4 Featured slots + Verified badge ─────────────────────────
  featuredPricing: () => client.get("/public/featured-pricing"),
  featuredQuote: (payload) => client.post("/featured/quote", payload),
  featuredPurchase: (payload) => client.post("/featured/purchase", payload),
  featuredConvertToPaid: (payload) =>
    client.post("/featured/convert-to-paid", payload),
  // Phase 18 Round 2A — wallet-debit path for featured slots.
  featuredPayWithWallet: (payload) =>
    client.post("/featured/pay-with-wallet", payload),
  featuredMine: () => client.get("/featured/mine"),
  // Admin/Super-admin
  adminSetVerified: (campaignId, isVerified) =>
    client.put(`/admin/campaigns/${campaignId}/verified`, {
      is_verified: !!isVerified,
    }),
  adminSetTrusted: (campaignId, isTrusted) =>
    client.put(`/admin/campaigns/${campaignId}/trusted`, {
      is_trusted: !!isTrusted,
    }),
  superadminSetFeaturedPricing: (payload) =>
    client.put("/superadmin/featured-pricing", payload),

  // ── 02/08 Static Offer listing-duration pricing ────────────────
  listingPricing: () => client.get("/public/listing-pricing"),
  listingPurchase: (payload) => client.post("/listing/purchase", payload),
  listingConvertToPaid: (payload) => client.post("/listing/convert-to-paid", payload),
  // Phase 18 Round 2A — wallet-debit path for paid listing durations.
  listingPayWithWallet: (payload) => client.post("/listing/pay-with-wallet", payload),
  superadminSetListingPricing: (payload) =>
    client.put("/superadmin/listing-pricing", payload),

  // ── 02/08 Public — city-scoped category counts (empty when no city) ─
  categoryCounts: (city) =>
    client.get("/public/category-counts", { params: { city: city || undefined } }),

  // ── 03/08 Popular Stores (city-scoped, admin-curated) ────────────
  publicPopularStores: (city, limit = 8) =>
    client.get("/public/popular-stores", { params: { city, limit } }),
  adminPopularStores: (city) =>
    client.get("/admin/popular-stores", { params: { city } }),
  adminPinPopularStore: (payload) =>
    client.put("/admin/popular-stores/pin", payload),
  // Convenience passthroughs so pages that need low-level access can
  // build URLs without importing the axios client directly.
  get: (path, opts) => client.get(path, opts),
  post: (path, data, opts) => client.post(path, data, opts),

  // uploads
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },

  // ===== Admin =====
  adminLogin: (data) => client.post("/admin/auth/login", data),
  adminMe: (token) =>
    client.get("/admin/auth/me", token ? withAdminToken(token) : undefined),
  adminOverview: () => client.get("/admin/dashboard/overview"),
  adminVendors: () => client.get("/admin/vendors"),
  adminVendor: (id) => client.get(`/admin/vendors/${id}`),
  // Guided-Setup intake — separate endpoint so admin/superadmin can
  // 404-gracefully render an empty state for vendors who never used it.
  adminVendorIntake: (id) => client.get(`/admin/vendors/${id}/intake`),
  adminIntakeResponses: (params = {}) =>
    client.get("/admin/intake-responses", { params }),

  // ── Lifecycle-email scheduler controls (admin + superadmin) ─────
  // Powers the /admin/scheduler UI. `runSchedulerNow` is guarded by a
  // client-side 60s cooldown inside the SchedulerControlsPage — the
  // server-side advisory lock is the actual safety net, this cooldown
  // just prevents impatient re-clicks.
  adminSchedulerStatus: () => client.get("/admin/scheduler/status"),
  adminSchedulerPause: (payload = {}) =>
    client.post("/admin/scheduler/pause", payload),
  adminSchedulerResume: () => client.post("/admin/scheduler/resume"),
  adminSchedulerRunNow: () => client.post("/admin/scheduler/run-now"),
  adminActivateVendor: (id) => client.post(`/admin/vendors/${id}/activate`),
  adminSetVendorAccess: (id, payload) =>
    client.post(`/admin/vendors/${id}/access`, payload),
  adminWaiveCampaign: (id, reason) =>
    client.post(`/admin/campaigns/${id}/waive`, { reason: reason || "" }),
  adminVendorCampaigns: (id) => client.get(`/admin/vendors/${id}/campaigns`),
  adminVendorLeads: (id) => client.get(`/admin/vendors/${id}/leads`),
  adminAllCampaigns: (params) => client.get("/admin/campaigns", { params }),
  adminDeactivateCampaign: (id, reason) =>
    client.post(`/admin/campaigns/${id}/deactivate`, { reason: reason || "" }),
  adminReactivateCampaign: (id) =>
    client.post(`/admin/campaigns/${id}/reactivate`),
  adminAddNote: (id, note) => client.post(`/admin/vendors/${id}/notes`, note),
  adminSupportList: () => client.get("/admin/support"),
  adminManualSend: (data) => client.post("/admin/messages/send", data),
  adminReports: () => client.get("/admin/reports"),
  // Customers (admin + superadmin)
  adminCustomers: (params) => client.get("/admin/customers", { params }),
  adminCustomersStats: (params) => client.get("/admin/customers/stats", { params }),
  adminCustomerByPhone: (phone) =>
    client.get(`/admin/customers/by-phone/${encodeURIComponent(phone)}`),

  // ── Contact / Enquiries ─────────────────────────────────────────
  // Public endpoints (no token) — the /contact page uses these.
  contactCategories: () => client.get("/contact/categories"),
  submitContact: (payload) => client.post("/contact", payload),
  // Admin-gated — Enquiries dashboard.
  adminListContactSubmissions: (params) =>
    client.get("/admin/contact-submissions", { params }),
  adminGetContactSubmission: (id) =>
    client.get(`/admin/contact-submissions/${id}`),
  adminUpdateContactSubmission: (id, payload) =>
    client.put(`/admin/contact-submissions/${id}`, payload),

  // ── Referral Codes (signup-time) ────────────────────────────────
  // List readable by any admin; create/update gated to super-admin.
  adminListReferralCodes: (params) =>
    client.get("/admin/referral-codes", { params }),
  saCreateReferralCode: (payload) =>
    client.post("/superadmin/referral-codes", payload),
  saUpdateReferralCode: (id, payload) =>
    client.put(`/superadmin/referral-codes/${id}`, payload),

  // ── Billing Coupons (checkout-time, campaign-fee only) ──────────
  adminListBillingCoupons: (params) =>
    client.get("/admin/billing-coupons", { params }),
  saCreateBillingCoupon: (payload) =>
    client.post("/superadmin/billing-coupons", payload),
  saUpdateBillingCoupon: (id, payload) =>
    client.put(`/superadmin/billing-coupons/${id}`, payload),
  // Vendor checkout — apply a coupon to the current pending payment.
  applyBillingCoupon: (campaignId, code) =>
    client.post(`/payments/campaigns/${campaignId}/checkout/apply-coupon`, { code }),

  // Read-only coupon validate — used by wallet top-up, listing
  // duration, and featured-slot flows to preview a coupon's
  // discount BEFORE creating a Razorpay order. Never mutates the
  // coupon's usage count. Backend delegates to core.coupons.
  previewBillingCoupon: (payload) =>
    client.post("/coupons/preview", payload),

  // ===== Super Admin =====
  superadminLoginStep1: (data) => client.post("/superadmin/auth/login", data),
  superadminVerifyOTP: (data) => client.post("/superadmin/auth/verify-otp", data),
  superadminAnalytics: () => client.get("/superadmin/dashboard/analytics"),
  adminPayments: (params) => client.get("/admin/payments", { params }),
  superadminSuspendVendor: (id, reason) =>
    client.post(`/superadmin/vendors/${id}/suspend`, { reason }),
  superadminReactivateVendor: (id) =>
    client.post(`/superadmin/vendors/${id}/reactivate`),
  superadminDeleteVendor: (id) => client.delete(`/superadmin/vendors/${id}`),
  superadminListAdmins: () => client.get("/superadmin/admins"),
  superadminCreateAdmin: (data) => client.post("/superadmin/admins", data),
  superadminSuspendAdmin: (id) =>
    client.post(`/superadmin/admins/${id}/suspend`),
  superadminDeleteAdmin: (id) => client.delete(`/superadmin/admins/${id}`),
  superadminGetPricing: () => client.get("/superadmin/pricing"),
  superadminSetPricing: (data) => client.put("/superadmin/pricing", data),
  superadminAdminLogs: () => client.get("/superadmin/admin-logs"),

  // ===== Coupon Verifier =====
  // Verifier accounts (superadmin-managed CRUD).
  superadminListVerifiers: () => client.get("/superadmin/verifiers"),
  superadminCreateVerifier: (data) => client.post("/superadmin/verifiers", data),
  superadminSuspendVerifier: (id) =>
    client.post(`/superadmin/verifiers/${id}/suspend`),
  superadminReactivateVerifier: (id) =>
    client.post(`/superadmin/verifiers/${id}/reactivate`),
  superadminDeleteVerifier: (id) =>
    client.delete(`/superadmin/verifiers/${id}`),
  // Wallet adjustments (refunds / credits / one-off debits).
  superadminAdjustWallet: (vendorId, payload) =>
    client.post(`/superadmin/vendors/${vendorId}/wallet/adjust`, payload),
  // Verifier self-serve.
  verifierLogin: (data) => client.post("/verify/login", data),
  verifierMe: () => client.get("/verify/me"),
  verifierSearch: (paramsOrQ, phone) => {
    // Backward-compat: still accepts (q, phone) for admin/verifier
    // callers; the vendor branch passes { phone, code } directly.
    const params =
      typeof paramsOrQ === "string"
        ? (phone ? { q: paramsOrQ, phone } : { q: paramsOrQ })
        : paramsOrQ;
    return client.get("/verify/search", { params });
  },
  verifierRedeem: (couponId) =>
    client.post(`/verify/coupons/${couponId}/redeem`),

  // ===== Coupon Pool (bulk-uploaded external codes) =====
  campaignPoolUpload: (campaignId, sliceId, codesText) =>
    client.post(
      `/campaigns/${campaignId}/slices/${sliceId}/coupon-pool/upload`,
      { codes: codesText },
    ),
  campaignPoolSummary: (campaignId, sliceId) =>
    client.get(`/campaigns/${campaignId}/slices/${sliceId}/coupon-pool`),

  // ── Customer-account endpoints (Phase-1) ────────────────────────
  customerSendOtp: (payload) => client.post("/customer/auth/send-otp", payload),
  customerVerifyOtp: (payload) => client.post("/customer/auth/verify-otp", payload),
  customerMe: () => client.get("/customer/auth/me"),
  customerUpdateProfile: (payload) => client.put("/customer/auth/me", payload),
  customerUpdateMe: (payload) => client.put("/customer/auth/me", payload),
  customerMyWinnings: () => client.get("/customer/my-winnings"),

  // ── Public + Offers ──────────────────────────────────────────────
  publicCampaigns: (params) => client.get("/public/campaigns", { params }),
  publicOfferDetail: (id) => client.get(`/public/campaigns/${id}/offer`),
  claimOffer: (id) => client.post(`/offers/${id}/claim`),

  // ── Cities (Phase-1 city selection system) ───────────────────────
  // Public endpoint powers the shared <CitySelect> picker used across
  // vendor profile, campaign wizard, Explore, and homepage.
  publicCities: () => client.get("/public/cities"),
  adminListCities: () => client.get("/admin/cities"),
  superadminCreateCity: (payload) => client.post("/superadmin/cities", payload),
  superadminUpdateCity: (id, payload) => client.put(`/superadmin/cities/${id}`, payload),

  // ── Franchise / City Operator ──────────────────────────────────
  superadminListFranchises: () => client.get("/superadmin/franchise-operators"),
  superadminCreateFranchise: (payload) => client.post("/superadmin/franchise-operators", payload),
  superadminUpdateFranchise: (id, payload) => client.put(`/superadmin/franchise-operators/${id}`, payload),
  franchiseStaffList: () => client.get("/franchise/staff"),
  franchiseStaffCreate: (payload) => client.post("/franchise/staff", payload),
  franchiseStaffUpdate: (id, payload) => client.put(`/franchise/staff/${id}`, payload),
  franchiseOverview: () => client.get("/franchise/overview"),
  franchiseSelfCheck: (qs) => client.get(`/franchise/self-check${qs ? `?${qs}` : ""}`),

  // Phase 20 — SuperAdmin city_id data-quality view
  superadminCityIntegrityList: () => client.get("/superadmin/data-quality/city-integrity"),
  superadminCityIntegrityResolve: (payload) =>
    client.post("/superadmin/data-quality/city-integrity/resolve", payload),
  franchiseActivity: (limit = 200) => client.get("/franchise/activity", { params: { limit } }),
  franchiseVendors: () => client.get("/franchise/vendors"),
  franchiseCampaigns: () => client.get("/franchise/campaigns"),
  franchiseEnquiries: () => client.get("/franchise/enquiries"),
  franchiseLeads: () => client.get("/franchise/leads"),
  franchiseSuspendVendor: (vid, reason) => client.post(`/franchise/vendors/${vid}/suspend`, { reason }),
  franchiseReactivateVendor: (vid) => client.post(`/franchise/vendors/${vid}/reactivate`),
  franchiseEnquiryStatus: (eid, payload) => client.post(`/franchise/enquiries/${eid}/status`, payload),
  franchiseSetVerified: (cid, val) => client.put(`/franchise/campaigns/${cid}/verified`, { is_verified: !!val }),
  franchiseSetTrusted: (cid, val) => client.put(`/franchise/campaigns/${cid}/trusted`, { is_trusted: !!val }),

  // City-scoped billing coupons (franchise-created; server forces
  // city_scope to caller's assigned_city).
  franchiseCreateBillingCoupon: (payload) => client.post("/franchise/billing-coupons", payload),
  franchiseListBillingCoupons: () => client.get("/franchise/billing-coupons"),

  // City revenue dashboard — reporting only, no payout.
  franchiseRevenue: (params) => client.get("/franchise/revenue", { params }),

  // Picker searches for the City Homepage editor.
  franchiseSearchCampaigns: (q) => client.get("/franchise/search/campaigns", { params: { q: q || "" } }),
  franchiseSearchVendors: (q) => client.get("/franchise/search/vendors", { params: { q: q || "" } }),

  // ── City Homepage content ──────────────────────────────────────
  adminGetCityHomepage: (city) => client.get(`/public/city-homepage/${encodeURIComponent(city)}`),
  publicCityHomepage: (city) => client.get(`/public/city-homepage/${encodeURIComponent(city)}`),
  publicHomepageDisplayCaps: (city) =>
    client.get("/public/homepage-display-caps", { params: city ? { city } : {} }),

  // Admin/Super Admin — read + update global default caps.
  adminGetHomepageCaps: () => client.get("/admin/homepage-caps"),
  adminUpdateHomepageCaps: (payload) => client.put("/admin/homepage-caps", payload),
  adminUpsertCityHomepage: (city, payload) => client.put(`/admin/city-homepage/${encodeURIComponent(city)}`, payload),

  // ── Banner Ads ─────────────────────────────────────────────────
  publicBannerAds: (zone) => client.get("/public/banner-ads", { params: { zone } }),
  bannerAdClick: (id) => client.post(`/public/banner-ads/${id}/click`),
  bannerAdImpression: (id) => client.post(`/public/banner-ads/${id}/impression`),
  adminListBanners: () => client.get("/admin/banner-ads"),
  superadminCreateBanner: (payload) => client.post("/superadmin/banner-ads", payload),
  superadminUpdateBanner: (id, payload) => client.put(`/superadmin/banner-ads/${id}`, payload),
  superadminDeleteBanner: (id) => client.delete(`/superadmin/banner-ads/${id}`),
};

export default api;
